import { useEffect, useRef, useState } from "react";
import { Sparkles, Languages, Loader2, AudioLines } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api, formatApiError } from "@/lib/api";
import { LANGS, useApp } from "@/lib/i18n";

export function useTranscriptTasks(meetingId, onDone) {
  const [tasks, setTasks] = useState([]);
  const wasRunning = useRef(false);
  const load = () => api.get(`/meetings/${meetingId}/transcript/tasks`).then((r) => setTasks(r.data));
  useEffect(() => { load(); }, [meetingId]); // eslint-disable-line react-hooks/exhaustive-deps
  const running = tasks.find((t) => t.status === "running");
  useEffect(() => {
    if (running) { wasRunning.current = true; const t = setTimeout(load, 3000); return () => clearTimeout(t); }
    if (wasRunning.current && tasks[0]) {
      wasRunning.current = false;
      if (tasks[0].status === "completed") onDone(tasks[0]);
      else toast.error(tasks[0].error || "Gagal");
    }
  }, [tasks]); // eslint-disable-line react-hooks/exhaustive-deps
  const start = async (path, body) => {
    try { await api.post(`/meetings/${meetingId}/transcript/${path}`, body); wasRunning.current = true; load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return { running, start };
}

export const TaskButtons = ({ meeting, running, start }) => {
  const { L } = useApp();
  const label = { polish: L("Memulihkan…", "Polishing…"), diarize: L("Mengesan suara…", "Detecting voices…") }[running?.kind] || L("Menterjemah…", "Translating…");
  if (running) return <Button size="sm" variant="outline" disabled className="rounded-xl" data-testid="transcript-task-running"><Loader2 size={14} className="mr-1 animate-spin" /> {label}</Button>;
  return (
    <>
      <Button size="sm" variant="outline" className="rounded-xl border-violet-200 text-violet-700 hover:bg-violet-50" onClick={() => start("enhance")} data-testid="transcript-enhance-btn">
        <Sparkles size={14} className="mr-1" /> {meeting.transcript_polished ? L("Pulihkan semula", "Re-polish") : L("Pulihkan dengan AI", "Polish with AI")}
      </Button>
      <Button size="sm" variant="outline" className="rounded-xl" onClick={() => start("diarize")} data-testid="transcript-diarize-btn" title={L("Kenal pasti pembicara melalui suara", "Identify speakers by voice")}>
        <AudioLines size={14} className="mr-1" /> {L("Kesan semula pembicara", "Re-detect speakers")}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild><Button size="sm" variant="outline" className="rounded-xl" data-testid="transcript-translate-btn"><Languages size={14} className="mr-1" /> {L("Terjemah", "Translate")}</Button></DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel className="text-xs font-normal text-slate-500">{L("Terjemah ke", "Translate to")}</DropdownMenuLabel>
          {LANGS.map(([k, ms, en]) => <DropdownMenuItem key={k} onClick={() => start("translate", { target: k })} data-testid={`translate-to-${k}`}>{L(ms, en)}</DropdownMenuItem>)}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
